import { useRef, useState } from 'react';

import { useAudioPlayer } from './AudioPlayerContext';

export function useAudioPlayerControls(audioId: string, audioUrl: string, mediaServiceUrl?: string) {
  const { activeAudioId, status, error, currentTime, duration, volume, playAudio, pauseAudio, resumeAudio, setVolume, seekTo, isExpanded, toggleExpand } =
    useAudioPlayer();

  const [isMuted, setIsMuted] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Check if this player is the active one
  const isActive = activeAudioId === audioId;

  // Format time (seconds) to MM:SS format
  const formatTime = (timeInSeconds: number): string => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle click on the progress bar to seek
  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (progressBarRef.current && status !== 'loading' && status !== 'error') {
      const rect = progressBarRef.current.getBoundingClientRect();
      const clickPosition = (e.clientX - rect.left) / rect.width;
      const newTime = clickPosition * duration;

      seekTo(newTime);
    }
  };

  // Handle volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);

    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  // Toggle mute
  const toggleMute = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isMuted) {
      setVolume(volume > 0 ? volume : 1);
      setIsMuted(false);
    } else {
      setVolume(0);
      setIsMuted(true);
    }
  };

  // Handle play button click
  const handlePlayClick = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!isActive) {
      // Start playing this audio
      await playAudio(audioId, audioUrl, mediaServiceUrl);
    } else {
      // Toggle play/pause for active audio
      if (status === 'playing') {
        pauseAudio();
      } else if (status === 'paused') {
        resumeAudio();
        // If the player is not expanded, expand it
        if (!isExpanded) {
          toggleExpand();
        }
      } else if (status === 'error') {
        // Retry on error
        await playAudio(audioId, audioUrl, mediaServiceUrl);
      }
    }
  };

  // Handle retry on error
  const handleRetry = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isActive && status === 'error') {
      await playAudio(audioId, audioUrl, mediaServiceUrl);
    }
  };

  // Calculate progress percentage
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Group related state and handlers
  const playerState = {
    status,
    error,
    currentTime,
    duration,
    volume,
    isExpanded,
    isMuted,
  };

  const handlers = {
    handlePlayClick,
    handleRetry,
    handleProgressBarClick,
    handleVolumeChange,
    toggleMute,
    seekTo,
    toggleExpand,
    pauseAudio,
  };

  const refs = {
    progressBarRef,
  };

  return {
    playerState,
    handlers,
    refs,
    isActive,
    progressPercentage,
    formatTime,
  };
}
